import { CSSProperties } from "react";

type PuzzleGridInPieceProps = {
  id?: string;
  text: string;
  textColors?: string;
  faded?: boolean;
  tableClassName?: string;
  modes?: ["crossword"];
  style?: CSSProperties;
};

type Clue = {
  indicator?: string;
};

type CellInfo = {
  clue?: Clue;
  innerText?: string;
  blackCell?: boolean;
  backgroundColor?: string;
};

const TEXT_COLOR_MAP = {
  Y: "var(--brown-90)",
  G: "var(--green-90)",
};

function parseText(text: string, showIndicator = false, textColors?: string) {
  let textLines = text.split("|");
  const textColorsLines = textColors?.split("|");

  let clueNum = 0;

  if (textLines.length == 1 && /^\d+x\d+$/.test(textLines[0])) {
    const dim = textLines[0].split("x");
    textLines = [];
    for (let r = 0; r < parseInt(dim[1]); r++) {
      textLines[r] = ".".repeat(parseInt(dim[0]));
    }
  }

  const cellInfos: CellInfo[][] = [];

  for (let r = 0; r < textLines.length; r++) {
    cellInfos.push([]);

    for (let c = 0; c < textLines[r].length; c++) {
      const cellInfo: CellInfo = {};

      const ch = textLines[r][c];

      if (ch == ".") {
        // do nothing
      } else if (ch == "#") {
        // do nothing
      } else if (ch == "@") {
        cellInfo.blackCell = true;
      } else {
        cellInfo.innerText = ch;
      }

      if (showIndicator && textLines[r][c] != "@") {
        const acrossClue =
          (c == 0 || textLines[r][c - 1] == "@") &&
          c < textLines[r].length - 1 &&
          textLines[r][c + 1] != "@"; // block/edge left, letter right
        const downClue =
          (r == 0 || textLines[r - 1][c] == "@") &&
          r < textLines.length - 1 &&
          textLines[r + 1][c] != "@"; // block/edge above, letter below

        if (acrossClue || downClue) {
          const clueIndicator = String(++clueNum);
          cellInfo.clue ??= {};
          cellInfo.clue.indicator = clueIndicator;
        }
      }

      if (textColorsLines) {
        const color = textColorsLines[r][c];
        if (color in TEXT_COLOR_MAP) {
          cellInfo.backgroundColor =
            TEXT_COLOR_MAP[color as keyof typeof TEXT_COLOR_MAP];
        }
      }

      cellInfos[r].push(cellInfo);
    }
  }

  return { cellInfos };
}

const PuzzleGridInPiece = (props: PuzzleGridInPieceProps) => {
  const {
    id,
    text,
    faded = false,
    tableClassName,
    modes,
    textColors,
    style,
  } = props;
  const { cellInfos } = parseText(
    text,
    modes?.includes("crossword"),
    textColors
  );

  return (
    <div
      className={`piece-wrapper noborder ${faded ? "piece-faded" : ""}`}
      style={
        {
          // empirically determined value
          "--puzzle-cell-size": `1.397em`,
          ...style,
        } as CSSProperties
      }
    >
      <div id={id} className="puzzle-entry loaded">
        <table
          className={`puzzle-grid-content no-input ${tableClassName ?? ""}`}
        >
          <tbody>
            {cellInfos.map((infoList, r) => (
              <tr key={r}>
                {infoList.map(
                  ({ clue, blackCell, innerText, backgroundColor }, c) => (
                    <td
                      key={`${r}-${c}`}
                      className={`cell inner-cell ${blackCell ? " black-cell unselectable" : ""}`}
                      style={{
                        backgroundColor,
                        borderTop: "1px solid black",
                        borderBottom: "1px solid black",
                        borderLeft: "1px solid black",
                        borderRight: "1px solid black",
                      }}
                    >
                      {clue ? (
                        <div aria-hidden="true" className="clue">
                          {clue.indicator}
                        </div>
                      ) : null}
                      <div aria-hidden="true" className="text">
                        <span>{innerText}</span>
                      </div>
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PuzzleGridInPiece;
